import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Form } from "@sparefoot/react-commons";
import { Button } from "components/core/Button";
import {
	MapPinFilledV2,
	MapPinFilled,
	LoadingBoxes
} from "components/core/Icons";
import { GoogleAutocomplete } from "components/core/GoogleAutocomplete";
import buildSearchLink from "utils/search/buildSearchLink";
import { TrackingClient } from "@sparefoot/tracking-client";
import { trackCTAClick } from "hooks/tracking/utils/clickTrackingUtils";

import "./SearchBar.scss";

const THEMES = {
	white: {
		borderlessInput: false,
		buttonTheme: "primary",
		loadingAnimationTheme: "blue"
	},
	lightBlue: {
		borderlessInput: true,
		buttonTheme: "secondary",
		loadingAnimationTheme: "green"
	},
	transparent: {
		borderlessInput: false,
		buttonTheme: "primary",
		loadingAnimationTheme: "blue"
	},
	lightBlue2: {
		borderlessInput: true,
		buttonTheme: "primary",
		loadingAnimationTheme: "blue"
	},
	lightBlue3: {
		borderlessInput: true,
		buttonTheme: "secondary",
		loadingAnimationTheme: "green"
	},
	lightBlueSmall: {
		borderlessInput: true,
		buttonTheme: "secondary",
		loadingAnimationTheme: "green"
	},
	stickyDarkBlue: {
		borderlessInput: true,
		buttonTheme: "primary",
		loadingAnimationTheme: "blue"
	},
	darkBlue: {
		borderlessInput: false,
		buttonTheme: "primary",
		loadingAnimationTheme: "blue"
	}
};

// locationParams: string (initial from prop) or object from GoogleAutocomplete { location, city?, state?, ... }
function getLocationDisplay(locationParams, fallback) {
	if (locationParams == null) return fallback ?? "";
	return typeof locationParams === "string"
		? locationParams
		: (locationParams.location ?? fallback ?? "");
}

export default function SearchBar({
	buttonText,
	id,
	location = "",
	children = null,
	showLabel = true,
	className,
	label,
	theme = "white",
	searchIn
}) {
	const [locationParams, setLocationParams] = useState(location);
	const [locating, setLocating] = useState(false);

	const themeConfig = THEMES[theme] ?? THEMES.white;
	const displayLocation = getLocationDisplay(locationParams, location);
	const hasLocationValue = (displayLocation ?? "").trim().length > 0;
	const searchInQuery = useMemo(
		() => (searchIn ? { searchType: "vehicle", vehicle: searchIn } : {}),
		[searchIn]
	);

	useEffect(() => {
		setLocationParams(location);
		setLocating(false);
	}, [location]);

	const handleSubmit = (event) => {
		event.preventDefault();
		setLocating(true);
		const params =
			typeof locationParams === "string"
				? { location: locationParams }
				: { ...locationParams };
		const url = buildSearchLink({ ...params, ...searchInQuery });
		TrackingClient.trackSearchSubmitted({
			search_filters: "storage",
			search_label: displayLocation,
			search_term: id,
			search_url: url
		});
		trackCTAClick(
			"search-box-id",
			"Search Button",
			"Find Storage",
			globalThis.location.href
		);
		globalThis.location.assign(url);
	};

	return (
		<Form
			className={classnames(
				"search-bar",
				className,
				`searchbar-${theme}`
			)}
			onSubmit={handleSubmit}
			successLabel="search-bar-success"
			asyncSubmit={false}
		>
			<div className="children-container">{children}</div>
			<div className={classnames("form-container", className)}>
				<GoogleAutocomplete
					id={id}
					type="text"
					label={label}
					className={classnames(
						"location-input",
						hasLocationValue && "not-empty-input"
					)}
					name="location"
					onChange={setLocationParams}
					showLabel={showLabel}
					prefix={showLabel ? null : <MapPinFilledV2 fill="blue" />}
					value={displayLocation}
					placeholder=""
					icon={<MapPinFilled fill="blue" />}
					borderlessInput={themeConfig.borderlessInput}
				/>
				<Button
					theme={themeConfig.buttonTheme}
					type="submit"
					className="search-button"
					fullWidth
				>
					{locating ? (
						<LoadingBoxes
							size="huge"
							primaryColor={themeConfig.loadingAnimationTheme}
						/>
					) : (
						buttonText
					)}
				</Button>
			</div>
		</Form>
	);
}

SearchBar.propTypes = {
	buttonText: PropTypes.string,
	id: PropTypes.string,
	location: PropTypes.string,
	children: PropTypes.node,
	className: PropTypes.string,
	label: PropTypes.string,
	theme: PropTypes.oneOf(Object.keys(THEMES)),
	searchIn: PropTypes.string,
	showLabel: PropTypes.bool
};
